import React, { useEffect, useState, useCallback } from "react"
import _ from 'lodash'

import { IafItemSvc, IafDataSource, IafPassSvc } from '@dtplatform/platform-api'
import { copyToClipboard } from "../utils/dev-mgmt-utils"

import './SandboxView.css'

//Secrets collection configuration
const SECRETS_COLLECTION = {
    NAME: 'API Keys',
    SHORT_NAME: 'keys',
    USER_TYPE: 'keys',
    DESCRIPTION: 'Encrypted storage for API keys'
};

// Secret field names (prefixed with . for encryption)
const SECRET_FIELDS = {
    CLIENT_ID: 'clientId',
    CLIENT_SECRET: '.clientSecret'
};

// Secret identifiers (for non-encrypted searchable field _name)
// Maps environment name to secret record name
const SECRET_NAMES = {
    SANDBOX: "Sandbox",
    TWINIT: "Prod"  // Twinit uses Prod credentials
};

const SANDBOX_API_URL = "https://sandbox-api.invicara.com"
const PROD_API_URL = "https://api.invicara.com"

/**
 * Derive organization user type from app user type by replacing "-apps" with "-orgs"
 * Examples: "sandbox-apps" -> "sandbox-orgs", "twinit-apps" -> "twinit-orgs"
 * Fallback: just add -orgs suffix (e.g., "apps" -> "apps-orgs")
 */
const getOrgsUserType = (appsUserType) => {
  if (appsUserType.endsWith('-apps')) {
    return appsUserType.replace('-apps', '-orgs')
  }
  // Fallback: just add -orgs suffix
  return `${appsUserType}-orgs`
}

/**
 * Get API URL based on handler config
 * Priority: handler.config.host > env-based fallback
 */
const getApiUrl = (handler) => {
  // First check for explicit host configuration
  if (handler?.config?.host) {
    return handler.config.host
  }

  // Fallback to env-based URL selection
  if (handler?.config?.env === 'Sandbox') {
    return SANDBOX_API_URL
  }

  // Default to production
  return PROD_API_URL
}

const SandboxView = ({ handler }) => {
  // Loading states
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingStatus, setIsFetchingStatus] = useState(false)

  // Data
  const [orgs, setOrgs] = useState([])
  const [apps, setApps] = useState([])

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState('apps') // 'apps' or 'orgs'

  // Selection
  const [selectedItem, setSelectedItem] = useState(null)
  const [liveStatus, setLiveStatus] = useState(null)
  const [liveStatusError, setLiveStatusError] = useState(null)

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadData = useCallback(async () => {
    setIsLoading(true)

    try {
      // Derive user types from handler config
      const appsUserType = handler.config.userType // e.g., "apps" or "twinit-apps"
      const orgsUserType = getOrgsUserType(appsUserType) // e.g., "orgs" or "twinit-orgs"

      // Load Organizations
      const orgsCollectionResp = await IafItemSvc.getNamedUserItems({ query: { _userType: orgsUserType } })
      let orgsResults = []

      if (orgsCollectionResp?._list?.length) {
        const orgsCollection = orgsCollectionResp._list[0]
        let offset = 0
        const pageSize = 100
        let total = 0
        let first = true

        while (first || orgsResults.length < total) {
          first = false
          const orgItems = await IafItemSvc.getRelatedItems(
            orgsCollection._userItemId,
            {},
            null,
            { page: { _offset: offset, _pageSize: pageSize }, sort: { "createResult._name": 1 } }
          )

          if (orgItems._list) {
            total = orgItems._total
            offset += pageSize
            orgsResults.push(...orgItems._list)
          }
        }
      }

      // Load Applications
      const appsCollectionResp = await IafItemSvc.getNamedUserItems({ query: { _userType: appsUserType } })
      let appsResults = []

      if (appsCollectionResp?._list?.length) {
        const appsCollection = appsCollectionResp._list[0]
        let offset = 0
        const pageSize = 100
        let total = 0
        let first = true

        while (first || appsResults.length < total) {
          first = false
          const appItems = await IafItemSvc.getRelatedItems(
            appsCollection._userItemId,
            {},
            null,
            { page: { _offset: offset, _pageSize: pageSize }, sort: { "createResult._name": 1 } }
          )

          if (appItems._list) {
            total = appItems._total
            offset += pageSize
            appsResults.push(...appItems._list)
          }
        }
      }

      setOrgs(orgsResults)
      setApps(appsResults)
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [handler.config.userType])

  useEffect(() => {
    loadData()
  }, [loadData])

  // ============================================================================
  // API HELPERS
  // ============================================================================

  async function getCredentials(handler) {
    // Get the encrypted secrets collection
    let collectionResp = await IafItemSvc.getNamedUserItems({
      query: { _userType: SECRETS_COLLECTION.USER_TYPE }
    });

    if (!collectionResp?._list?.[0]) {
      throw new Error("Secrets collection not found. Please configure API credentials first.");
    }

    const collection = collectionResp._list[0];

    // Load secrets as related items
    const secretsResp = await IafItemSvc.getRelatedItems(
      collection._userItemId,
      {},
      undefined,
      { page: { _pageSize: 100 } }
    );

    const secrets = secretsResp._list || [];

    // Determine which secret to use based on environment
    // handler.config.env can be "Sandbox", "Twinit", etc.
    const env = handler?.config?.env || "Sandbox";
    const secretName = SECRET_NAMES[env.toUpperCase()] || env.toUpperCase();

    const keys = secrets.find(s => s._name === secretName);
    const clientSecret = keys?.[SECRET_FIELDS.CLIENT_SECRET];

    if (!clientSecret) {
      throw new Error(`${secretName} credentials not found in encrypted storage. Please configure them first.`);
    }

    return {
      clientId: keys[SECRET_FIELDS.CLIENT_ID] || "appcreate",
      clientSecret: keys[SECRET_FIELDS.CLIENT_SECRET]
    };
  }

  const getToken = async (scope = 'read write') => {
    const { clientId, clientSecret } = await getCredentials(handler)
    const apiUrl = getApiUrl(handler)

    const authResult = await IafDataSource.executeFetchOrchestrator({
      _url: `${apiUrl}/passportsvc/api/v1/oauth/token?grant_type=client_credentials&scope=${encodeURIComponent(scope)}`,
      _auth: {
        _type: "BasicAuth",
        _params: { _username: clientId, _password: clientSecret }
      },
      _args: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      }
    })

    return authResult.rest_conn_result.access_token
  }

  const fetchLiveOrgStatus = async (orgId, useCurrentUserToken = false) => {
    const apiUrl = getApiUrl(handler)

    // If useCurrentUserToken is true, use the current user's context token
    // Otherwise, get token via credentials (which may have limited read permissions)
    const authConfig = useCurrentUserToken
      ? { _type: "ContextToken" }
      : { _type: "BearerToken", _params: { _token: await getToken('read write') } }

    const result = await IafDataSource.executeFetchOrchestrator({
      _url: `${apiUrl}/passportsvc/api/v1/organizations/${orgId}`,
      _auth: authConfig,
      _args: {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      }
    })
    return result.rest_conn_result;
  }

  const fetchLiveAppStatus = async (appId, useCurrentUserToken = false) => {
    const apiUrl = getApiUrl(handler)

    // If useCurrentUserToken is true, use the current user's context token
    // Otherwise, get token via credentials (which may have limited permissions)
    const authConfig = useCurrentUserToken
      ? { _type: "ContextToken" }
      : { _type: "BearerToken", _params: { _token: await getToken('application-create') } }

    const result = await IafDataSource.executeFetchOrchestrator({
      _url: `${apiUrl}/passportsvc/api/v1/apps/${appId}`,
      _auth: authConfig,
      _args: {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      }
    })
    return result.rest_conn_result;
  }

  // ============================================================================
  // CLEANUP HANDLERS
  // ============================================================================

  const cleanupDryRunLogs = async () => {
    if (!window.confirm('Are you sure you want to delete all test/dryRun logs? This cannot be undone.')) {
      return
    }

    try {
      // Derive user types from handler config
      const appsUserType = handler.config.userType
      const orgsUserType = getOrgsUserType(appsUserType)

      // Delete dryRun organizations
      const dryRunOrgs = orgs.filter(o => o._dryRun || o.createResult?._dryRun)
      if (dryRunOrgs.length > 0) {
        const orgsCollectionResp = await IafItemSvc.getNamedUserItems({ query: { _userType: orgsUserType } })
        if (orgsCollectionResp?._list?.[0]) {
          const orgsCollection = orgsCollectionResp._list[0]
          await Promise.all(
            dryRunOrgs.map(org =>
              IafItemSvc.deleteRelatedItem(orgsCollection._userItemId, org._id)
            )
          )
        }
      }

      // Delete dryRun applications
      const dryRunApps = apps.filter(a => a._dryRun || a.createResult?._dryRun)
      if (dryRunApps.length > 0) {
        const appsCollectionResp = await IafItemSvc.getNamedUserItems({ query: { _userType: appsUserType } })
        if (appsCollectionResp?._list?.[0]) {
          const appsCollection = appsCollectionResp._list[0]
          await Promise.all(
            dryRunApps.map(app =>
              IafItemSvc.deleteRelatedItem(appsCollection._userItemId, app._id)
            )
          )
        }
      }

      // Clear selection if it was a dryRun item
      if (selectedItem && (selectedItem._dryRun || selectedItem.createResult?._dryRun)) {
        setSelectedItem(null)
        setLiveStatus(null)
      }

      // Reload data
      await loadData()

      alert(`Cleaned up ${dryRunOrgs.length} test organizations and ${dryRunApps.length} test applications`)
    } catch (err) {
      console.error('Failed to cleanup dryRun logs:', err)
      alert('Failed to cleanup test logs: ' + err.message)
    }
  }

  // ============================================================================
  // SELECTION HANDLERS
  // ============================================================================

  const selectItem = async (item, type) => {
    setSelectedItem({ ...item, _type: type })
    setLiveStatus(null)
    setLiveStatusError(null)
  }

  const fetchCurrentStatus = async (useCurrentUserToken = false) => {
    if (!selectedItem) return

    setIsFetchingStatus(true)
    setLiveStatusError(null)

    try {
      let status
      if (selectedItem._type === 'org') {
        const orgId = selectedItem.createResult?._id || selectedItem._id
        status = await fetchLiveOrgStatus(orgId, useCurrentUserToken)
      } else {
        const appId = selectedItem.createResult?._id || selectedItem._id
        status = await fetchLiveAppStatus(appId, useCurrentUserToken)
      }
      setLiveStatus(status)
      console.log('Live status:', status)
    } catch (err) {
      console.error('Failed to fetch live status:', err)
      setLiveStatusError(err.message || 'Failed to fetch current status from API')
    } finally {
      setIsFetchingStatus(false)
    }
  }

  // ============================================================================
  // FILTERING
  // ============================================================================

  const getFilteredItems = () => {
    const items = viewMode === 'orgs' ? orgs : apps
    if (!searchQuery.trim()) return items

    const query = searchQuery.toLowerCase()
    return items.filter(item => {
      const name = (item.createResult?._name || '').toLowerCase()
      const owner = getOwnerDisplay(item, viewMode === 'orgs').toLowerCase()
      return name.includes(query) || owner.includes(query)
    })
  }

  const getOrgForApp = (app) => {
    const orgId = app.createResult?._orgId || app._orgId
    if (!orgId) return null

    const orgLog = orgs.find(o => (o.createResult?._id || o._orgId) === orgId)

    // If we have an orgId but no org create log, return a placeholder with the ID
    if (!orgLog) {
      return {
        _id: orgId,
        _hasNoLog: true,
        createResult: { _id: orgId, _name: `Org ${orgId.substring(0, 8)}...` }
      }
    }

    return orgLog
  }

  const getAppsCountForOrg = (orgId) => {
    return apps.filter(a => (a.createResult?._orgId || a._orgId) === orgId).length
  }

  const getOwnerDisplay = (item, isOrg) => {
    if (isOrg) {
      return item._orgOwner || item.createResult?._orgOwner || ''
    }
    // For apps: check _appOwnerDetails first, then _appOwner
    if (item.createResult?._appOwnerDetails) {
      const details = item.createResult._appOwnerDetails
      return details._email || `${details._firstname || ''} ${details._lastname || ''}`.trim()
    }
    return item.createResult?._appOwner || item._appOwner || ''
  }

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  const filteredItems = getFilteredItems()

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="sandbox-browser">
      {/* Header */}
      <div className="sandbox-header">
        <div className="header-title">
          <i className="fas fa-archive"></i>
          <h1>Sandbox Browser</h1>
        </div>
        <p className="header-subtitle">
          Browse creation logs for {handler.config.env || 'Sandbox'} applications and organizations
        </p>
        {(orgs.some(o => o._dryRun || o.createResult?._dryRun) ||
          apps.some(a => a._dryRun || a.createResult?._dryRun)) && (
          <button className="btn-cleanup" onClick={cleanupDryRunLogs}>
            <i className="fas fa-trash-alt"></i> Clean Up Test Logs
          </button>
        )}
      </div>

      <div className="sandbox-content">
        {/* Master Panel - List */}
        <div className="master-panel">
          {/* View Toggle */}
          <div className="view-tabs">
            <button
              className={viewMode === 'apps' ? 'active' : ''}
              onClick={() => { setViewMode('apps'); setSelectedItem(null); setLiveStatus(null) }}
            >
              <i className="fas fa-cube"></i>
              Applications
              <span className="count">{apps.length}</span>
            </button>
            <button
              className={viewMode === 'orgs' ? 'active' : ''}
              onClick={() => { setViewMode('orgs'); setSelectedItem(null); setLiveStatus(null) }}
            >
              <i className="fas fa-building"></i>
              Organizations
              <span className="count">{orgs.length}</span>
            </button>
          </div>

          {/* Search */}
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={`Search ${viewMode === 'orgs' ? 'organizations' : 'applications'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {/* List */}
          <div className="items-list">
            {isLoading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading {viewMode}...</span>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No {viewMode === 'orgs' ? 'organizations' : 'applications'} found</p>
              </div>
            ) : (
              filteredItems.map((item) => {
                const isSelected = selectedItem?._id === item._id
                const isOrg = viewMode === 'orgs'
                const name = item.createResult?._name || item.createRequest?._name || 'Unnamed'
                const owner = getOwnerDisplay(item, isOrg)
                const org = !isOrg ? getOrgForApp(item) : null
                const isDryRun = item._dryRun || item.createResult?._dryRun

                return (
                  <div
                    key={item._id}
                    className={`item-card ${isSelected ? 'selected' : ''} ${isDryRun ? 'dry-run' : ''}`}
                    onClick={() => selectItem(item, isOrg ? 'org' : 'app')}
                  >
                    <div className="item-main">
                      <div className="item-name">{name}</div>
                      <div className="item-owner">{owner}</div>
                    </div>
                    <div className="item-meta">
                      {isOrg ? (
                        <>
                          <span className="badge apps-count">
                            {getAppsCountForOrg(item.createResult?._id || item._orgId)} apps
                          </span>
                          {item.configResult && (
                            <span className="badge config">
                              <i className="fas fa-cog"></i>
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          {org ? (
                            <span
                              className={`badge ${org._hasNoLog ? 'org-no-log' : 'org-name'}`}
                              title={org._hasNoLog ? `Org ID: ${org._id} (no create log found)` : org.createResult?._name}
                            >
                              {org._hasNoLog ? '⚠ ' : ''}{org.createResult?._name?.substring(0, 15) || 'Org'}
                            </span>
                          ) : (
                            <span className="badge pre-org" title="Created before organization system">
                              Pre-Org
                            </span>
                          )}
                        </>
                      )}
                      {isDryRun && (
                        <span className="badge dry-run">
                          <i className="fas fa-flask"></i> Test
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Summary */}
          {!isLoading && (
            <div className="list-summary">
              Showing {filteredItems.length} of {viewMode === 'orgs' ? orgs.length : apps.length} {viewMode}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="detail-panel">
          {!selectedItem ? (
            <div className="no-selection">
              <i className="fas fa-mouse-pointer"></i>
              <h3>Select an item</h3>
              <p>Choose an {viewMode === 'orgs' ? 'organization' : 'application'} from the list to view its creation logs</p>
            </div>
          ) : (
            <>
              {/* Item Header */}
              <div className="detail-header">
                <div className="detail-icon">
                  <i className={`fas ${selectedItem._type === 'org' ? 'fa-building' : 'fa-cube'}`}></i>
                </div>
                <div className="detail-title">
                  <h2>{selectedItem.createResult?._name || 'Unnamed'}</h2>
                  <span className="detail-type">
                    {selectedItem._type === 'org' ? 'Organization' : 'Application'}
                  </span>
                </div>
                <div className="fetch-status-buttons">
                  <button
                    className="btn-primary"
                    onClick={() => fetchCurrentStatus(false)}
                    disabled={isFetchingStatus || (!selectedItem._id)}
                    title={!selectedItem._id ? 'No ID available for API lookup' : 'Fetch using API credentials (limited permissions)'}
                  >
                    {isFetchingStatus ? (
                      <><i className="fas fa-spinner fa-spin"></i> Fetching...</>
                    ) : (
                      <><i className="fas fa-sync-alt"></i> Fetch via Credentials</>
                    )}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => fetchCurrentStatus(true)}
                    disabled={isFetchingStatus || (!selectedItem._id)}
                    title={!selectedItem._id ? 'No ID available for API lookup' : 'Fetch using your user token (requires owner permissions)'}
                  >
                    {isFetchingStatus ? (
                      <><i className="fas fa-spinner fa-spin"></i> Fetching...</>
                    ) : (
                      <><i className="fas fa-user-shield"></i> Fetch as Owner</>
                    )}
                  </button>
                </div>
              </div>

              {/* Quick Info */}
              <div className="quick-info">
                <div className="info-item">
                  <span className="info-label">Owner</span>
                  <span className="info-value">
                    {getOwnerDisplay(selectedItem, selectedItem._type === 'org')}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">ID</span>
                  <span className="info-value code">
                    {selectedItem._type === 'org' ? selectedItem._id : selectedItem._id || 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created</span>
                  <span className="info-value">{formatDate(selectedItem.createdAt)}</span>
                </div>
                {selectedItem._type === 'app' && (
                  <div className="info-item">
                    <span className="info-label">Organization</span>
                    <span className="info-value">
                      {getOrgForApp(selectedItem)?.createResult?._name || (
                        <span className="text-muted">
                          {(selectedItem.createResult?._orgId || selectedItem._orgId) ? 'Unknown' : 'Pre-Organization Era'}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {selectedItem._type === 'org' && (
                  <div className="info-item">
                    <span className="info-label">Applications</span>
                    <span className="info-value">{getAppsCountForOrg(selectedItem.createResult?._id || selectedItem._orgId)}</span>
                  </div>
                )}
              </div>

              {/* Logs Section */}
              <div className="logs-section">
                <div className="logs-header">
                  <h3><i className="fas fa-history"></i> Creation Logs</h3>
                  <span className="logs-note">Historical data from when this resource was created</span>
                </div>

                {/* Request Log */}
                <div className="log-block">
                  <div className="log-title">
                    <span><i className="fas fa-arrow-up"></i> Request</span>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(JSON.stringify(selectedItem.createRequest, null, 2), 'creation request')}
                      title="Copy to clipboard"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <pre className="log-content">
                    {selectedItem.createRequest
                      ? JSON.stringify(selectedItem.createRequest, null, 2)
                      : 'No request data available'}
                  </pre>
                </div>

                {/* Response Log */}
                <div className="log-block">
                  <div className="log-title">
                    <span><i className="fas fa-arrow-down"></i> Response</span>
                    <button
                      className="btn-copy"
                      onClick={() => copyToClipboard(JSON.stringify(selectedItem.createResult, null, 2), 'creation response')}
                      title="Copy to clipboard"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <pre className="log-content">
                    {selectedItem.createResult
                      ? JSON.stringify(selectedItem.createResult, null, 2)
                      : 'No response data available'}
                  </pre>
                </div>

                {/* Config Log (for orgs) */}
                {selectedItem._type === 'org' && selectedItem.configResult && (
                  <div className="log-block">
                    <div className="log-title">
                      <span><i className="fas fa-cog"></i> Configuration</span>
                      <button
                        className="btn-copy"
                        onClick={() => copyToClipboard(JSON.stringify(selectedItem.configResult, null, 2), 'configuration')}
                        title="Copy to clipboard"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                    <pre className="log-content">
                      {JSON.stringify(selectedItem.configResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Live Status Section */}
              {(liveStatus || liveStatusError) && (
                <div className="live-status-section">
                  <div className="logs-header">
                    <h3><i className="fas fa-satellite-dish"></i> Live Status</h3>
                    <span className="logs-note">Current state from Sandbox API</span>
                  </div>

                  {liveStatusError ? (
                    <div className="status-error">
                      <i className="fas fa-exclamation-circle"></i>
                      <span>{liveStatusError}</span>
                    </div>
                  ) : (
                    <div className="log-block live">
                      <div className="log-title">
                        <span><i className="fas fa-check-circle"></i> Current State</span>
                        <button
                          className="btn-copy"
                          onClick={() => copyToClipboard(JSON.stringify(liveStatus, null, 2), 'live status')}
                          title="Copy to clipboard"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                      <pre className="log-content">
                        {JSON.stringify(liveStatus, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default SandboxView
